import { useEffect, useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import type { SearchSchemaInput } from '@tanstack/react-router';
import { requireCapability } from '@/lib/route-guards';
import { nyEndOfDayMsExclusive, nyStartOfDayMs } from '@/lib/datetime';
import { warm, warmBackground } from '@/api/loaders';
import { campaignOverview, campaignsList, productionsList } from '@/api/resources';
import {
  parseCampaignsOverviewSearch,
  resolveCampaignsOverviewSearch,
  serializeCampaignsOverviewSearch,
  updateCampaignsOverviewSearch,
  withoutDefaultedRange,
} from '@/lib/campaigns-overview-search';
import type {
  CampaignsOverviewSearchInput,
  CampaignsOverviewSearchPatch,
} from '@/lib/campaigns-overview-search';
import { buildCampaignOverviewRequest } from '@/lib/campaigns-overview-request';
import { Dashboard } from '@/screens/dashboard';

export const Route = createFileRoute('/campaigns/')({
  beforeLoad: requireCapability('campaigns_view'),
  validateSearch: (search: CampaignsOverviewSearchInput & SearchSchemaInput) =>
    parseCampaignsOverviewSearch(search),
  search: {
    // The router writes validated search back into the URL on every
    // navigation, defaults included. A defaulted range must not be written:
    // a written date is a pinned date, and this tab has to keep asking for
    // the last 30 completed New York days after ET midnight, not the 30 that
    // were completed when it was opened.
    middlewares: [({ search, next }) => withoutDefaultedRange(next(search))],
  },
  loaderDeps: ({ search }) => search,
  // Search-driven route: no skeleton for an in-screen navigation (router.ts).
  pendingMs: Infinity,
  loader: ({ context, deps }) => {
    warmBackground(context.queryClient, campaignsList({}), productionsList({}));
    // Resolved at load time, so the slot warmed here is the one the screen
    // reads on the same New York day.
    return warm(
      context.queryClient,
      campaignOverview(buildCampaignOverviewRequest(deps)),
    );
  },
  component: CampaignsPage,
});

/** The start of the current New York day, re-read when the day rolls over. */
function useNyDayStart(): number {
  const [dayStart, setDayStart] = useState(() => nyStartOfDayMs(Date.now()));
  useEffect(() => {
    // One timer to the next NY midnight, plus a second of slack: a timer may
    // fire late but must never resolve the old day again, since an unchanged
    // state would not re-arm it. Nothing else on this page changes at that
    // instant, so this is the whole clock.
    const wake = nyEndOfDayMsExclusive(dayStart) - Date.now() + 1_000;
    const timer = setTimeout(
      () => setDayStart(nyStartOfDayMs(Date.now())),
      Math.max(wake, 0),
    );
    return () => clearTimeout(timer);
  }, [dayStart]);
  return dayStart;
}

function CampaignsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const dayStart = useNyDayStart();
  // A defaulted range is re-derived per New York day, so the request key and
  // the date picker roll over together; an explicit range keeps its identity.
  const resolved = useMemo(
    () => resolveCampaignsOverviewSearch(search, dayStart),
    [search, dayStart],
  );
  return (
    <Dashboard
      search={resolved}
      onSearchChange={(
        patch: CampaignsOverviewSearchPatch,
        options?: { replace?: boolean },
      ) => {
        void navigate({
          search: (current) => serializeCampaignsOverviewSearch(
            updateCampaignsOverviewSearch(current, patch),
          ),
          replace: options?.replace,
        });
      }}
    />
  );
}
